import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, verticalListSortingStrategy, } from "@dnd-kit/sortable";

import { Button, DatePicker, Drawer, Form, Input, message, Upload } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import TableCustom from "../../../components/ui/TableCustom";
import { Colors } from "../../../constants/Colors";
import DrawerFooter from "../../../components/layout/drawer/DrawerFooter";
import requiredRule from "../../../utils/formRules/requiredRule";
import GeneralModal from "../../../utils/GeneralModal";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import DraggableTableRow from "../components/draggableTableRow";
import fileToBase64 from "../utils/fileToBase64";
import { createUploadProps } from "../utils/uploadProps";
import createChannelVideoColumns from "./content/channelColumns";
import { MethodType, VideoType } from "./content/channelTypes";
import { channelVideoDelete, channelVideoMutation, fetchChannelVideosById, updateChannelVideosSort } from "./services/channel.services";

interface ChannelTabContentProps {
  channelId: number;
  channelName: string;
  userName: string;
}

interface FilterType {
  text: string;
  value: string;
}

const ChannelTabContent = ({
  channelId,
  channelName,
  userName
}: ChannelTabContentProps) => {
  const [dateFilter, setDateFilter] = useState<FilterType[]>([]);
  const [displayData, setDisplayData] = useState<VideoType[] | any>([]);
  const [editData, setEditData] = useState<VideoType | undefined>(undefined);
  const [deleteId, setDeleteId] = useState<number>(0);
  const [modalAction, setModalAction] = useState<"Create" | "Edit" | "Delete">("Create");
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDragged, setIsDragged] = useState(false);
  const [form] = Form.useForm();


  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setIsDragged(true);
      setDisplayData((previous: VideoType[]) => {
        const activeIndex = previous.findIndex((i) => i.videoId === active.id);
        const overIndex = previous.findIndex((i) => i.videoId === over?.id);
        return arrayMove(previous, activeIndex, overIndex);
      });
    }
  };

  const handleEditClick = (record: VideoType) => {
    setShowDrawer(true);
    setModalAction("Edit");
    setEditData(record);
  };

  const getVideosList = async () => {
    const data = await fetchChannelVideosById(channelId);

    if (data) {
      const uniqueVideoTitles = [
        ...new Set<string>(data.data.map((video: VideoType) => video.videoTitle)),
      ].sort((a, b) => a.localeCompare(b));

      const filterFormVideoTitles = uniqueVideoTitles.map((item) => ({
        text: item,
        value: item,
      }));

      setDateFilter(filterFormVideoTitles);
      setDisplayData(data.data);
    }
  }

  const handleSort = async () => {
    const payload = { videoIds: displayData.map((i: VideoType) => i.videoId) };

    const data = await updateChannelVideosSort(channelId, payload);

    if (data?.status === "SUCCESS") {
      getVideosList();
      handleClose();
      closeDeleteModal();
      message.success(renderAntDMessageConfig("Successful"));
    } else {
      message.error(renderAntDMessageConfig("Failed"));
    }

    setIsDragged(false);
  }

  const handleClose = () => {
    form.resetFields();
    setEditData(undefined);
    setShowDrawer(false);
  }

  const handleDeleteClick = (videoId: number) => {
    setShowDeleteModal(true);
    setDeleteId(videoId);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  }

  const handleDelete = async () => {
    const data = await channelVideoDelete(deleteId);

    if (data?.status === "SUCCESS") {
      getVideosList();
      handleClose();
      closeDeleteModal();
      message.success(renderAntDMessageConfig("Successful"));
    } else {
      message.error(renderAntDMessageConfig("Failed"));
    }
  }

  const handleSubmit = async (values: any) => {
    let method: MethodType;
    let payload;
    let videofile;
    let videoThumbnailfile;

    const video = values.videoFile[0];
    const videoThumbnail = values.videoThumbnailFile[0];

    if (video.originFileObj) {
      const base64File = await fileToBase64(video.originFileObj);
      videofile = {
        fileName: video.name,
        base64File
      }
    } else {
      videofile = {
        fileName: video.name
      }
    }

    if (videoThumbnail.originFileObj) {
      const base64File = await fileToBase64(videoThumbnail.originFileObj);
      videoThumbnailfile = {
        fileName: videoThumbnail.name,
        base64File
      }
    } else {
      videoThumbnailfile = {
        fileName: videoThumbnail.name
      }
    }

    if (modalAction === "Create") {
      method = "POST";
      payload = {
        channelId: channelId,
        videoTitle: values.videoTitle,
        videoFile: videofile,
        videoThumbnailFile: videoThumbnailfile,
        ...(values.startDate && { startDate: dayjs(values.startDate).format("YYYY-MM-DD") }),
        ...(values.endDate && { endDate: dayjs(values.endDate).format("YYYY-MM-DD") }),
        userName: userName,
      };
    } else {
      method = "PATCH";
      payload = {
        videoId: values.videoId,
        videoTitle: values.videoTitle,
        videoFile: videofile,
        videoThumbnailFile: videoThumbnailfile,
        ...(values.startDate && { startDate: dayjs(values.startDate).format("YYYY-MM-DD") }),
        ...(values.endDate && { endDate: dayjs(values.endDate).format("YYYY-MM-DD") }),
        userName: userName,
      };
    }

    const data = await channelVideoMutation(method, payload);

    if (data?.status === "SUCCESS") {
      getVideosList();
      handleClose();
      message.success(renderAntDMessageConfig("Successful"));
    } else {
      message.error(renderAntDMessageConfig("Failed"));
    }
  };

  useEffect(() => {
    getVideosList();
  }, []);

  useEffect(() => {
    if (isDragged) handleSort();
  }, [isDragged]);

  useEffect(() => {
    if (editData) {
      form.setFieldsValue({
        videoId: editData.videoId,
        videoTitle: editData.videoTitle,
        videoFile: [
          {
            uid: "0",
            name: editData.video,
            status: "done",
            url: editData.videoUrl
          },
        ],
        videoThumbnailFile: [
          {
            uid: "0",
            name: editData.videoThumbnail,
            status: "done",
            url: editData.videoThumbnailUrl
          },
        ],
        startDate: editData.startDate ? dayjs(editData.startDate) : "",
        endDate: editData.endDate ? dayjs(editData.endDate) : "",
      });
    }
  }, [editData, form]);

  return (
    <>
      <div className="table-container">
        <div className="table-menu-container">
          <p className="text-base font-medium">Total {displayData.length} videos</p>
          <Button
            type="primary"
            style={{ marginLeft: 8 }}
            icon={<PlusOutlined />}
            onClick={() => {
              setModalAction("Create");
              setShowDrawer(true);
              setEditData(undefined);
            }}
          >
            Add New Video
          </Button>
        </div>
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={displayData.map((i: VideoType) => i.videoId)}
            strategy={verticalListSortingStrategy}
          >
            <TableCustom
              rowKey={(record: VideoType) => record.videoId}
              columns={createChannelVideoColumns(dateFilter, handleEditClick, handleDeleteClick)}
              dataSource={displayData}
              pagination={false}
              components={{
                body: {
                  row: DraggableTableRow,
                },
              }}
            />
          </SortableContext>
        </DndContext>
      </div>
      <div>
        <Drawer
          title={modalAction === "Create" ? "Create new video" : "Edit the video"}
          open={showDrawer}
          onClose={() => handleClose()}
          width={500}
        >
          <Form
            name="theme-form"
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
          >
            <Form.Item
              name="videoId"
              hidden
            >
              <Input />
            </Form.Item>
            <Form.Item
              className="form-item"
              label="Category"
              name="category"
            >
              <p style={{ marginTop: -16, color: Colors.notEditableText }}>
                {channelName}
              </p>
            </Form.Item>
            <Form.Item
              name="videoId"
              hidden
            >
              <Input />
            </Form.Item>

            <Form.Item
              className="form-item"
              label="Video title"
              name="videoTitle"
              rules={[requiredRule]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Video"
              name="videoFile"
              className="form-item"
              valuePropName="fileList"
              rules={[requiredRule]}
              getValueFromEvent={(e) => e.fileList}
            >
              <Upload
                {...createUploadProps("mp4", "videoFile", 1)}
              >
                <Button icon={<UploadOutlined />}>Upload</Button>
              </Upload>
            </Form.Item>
            <Form.Item
              label="Video thumbnail"
              name="videoThumbnailFile"
              className="form-item"
              valuePropName="fileList"
              rules={[requiredRule]}
              getValueFromEvent={(e) => e.fileList}
            >
              <Upload
                {...createUploadProps("jpg", "videoThumbnailFile", 1)}
              >
                <Button icon={<UploadOutlined />}>Upload</Button>
              </Upload>
            </Form.Item>
            <Form.Item
              className="form-item"
              label="Start Date"
              name="startDate"
            >
              <DatePicker
                format="YYYY-MM-DD" />
            </Form.Item>
            <Form.Item
              className="form-item"
              label="EndDate"
              name="endDate"
            >
              <DatePicker
                format="YYYY-MM-DD" />
            </Form.Item>
            <DrawerFooter
              handleCancel={() => {
                form.resetFields();
                setEditData(undefined);
                setShowDrawer(false);
              }}
              handleDelete={modalAction === "Edit"
                ? () => handleDeleteClick(editData?.videoId!)
                : undefined}
            />
          </Form>
        </Drawer>
      </div>
      <GeneralModal
        title={"Are you sure you want to delete the item?"}
        text={""}
        open={showDeleteModal}
        close={closeDeleteModal}
        handleDelete={() => handleDelete()}
        isConfirm={false}
        isDeletion={true}
        width={417}
      />
    </>
  );
};

export default ChannelTabContent;
